import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    FaMicrosoft, FaReact, FaDatabase, FaMobile,
    FaCloud, FaCog, FaCode, FaChartBar, FaArrowRight, FaRobot
} from 'react-icons/fa';
import './ServicesPage.css';

const ServicesPage: React.FC = () => {
    const navigate = useNavigate();
    const [scrollY, setScrollY] = useState(0);

    useEffect(() => {
        const handleScroll = () => setScrollY(window.scrollY);
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const services = [
        {
            icon: <FaMicrosoft />,
            title: 'Microsoft 365 & Power Platform',
            desc: 'Complete SharePoint design, migration, and governance. Power Apps, Power Automate, Power BI, and Microsoft Copilot implementations for intelligent business automation.',
            tags: ['SharePoint Migration', 'Power Apps', 'Power Automate', 'Power BI', 'Copilot Integration', 'Office 365'],
        },
        {
            icon: <FaReact />,
            title: 'Website Development & Modern UI',
            desc: 'Cutting-edge web applications using .NET and React.js with seamless API integration, responsive design, and optimised cloud deployment pipelines.',
            tags: ['React.js Apps', '.NET Web Apps', 'API Integration', 'Responsive Design', 'Cloud Deployment'],
        },
        {
            icon: <FaDatabase />,
            title: 'Database Design & Management',
            desc: 'Comprehensive database solutions including architecture design, SQL Server, MySQL, and Dataverse with migration and performance optimisation strategies.',
            tags: ['Database Architecture', 'SQL Server', 'MySQL', 'Dataverse', 'Performance Tuning'],
        },
        {
            icon: <FaMobile />,
            title: 'Mobile App Development',
            desc: 'Cross-platform mobile applications built with Flutter, featuring native performance, API integration, payment systems, and enterprise-grade security.',
            tags: ['Flutter Development', 'Cross-Platform', 'API Integration', 'Payment Systems', 'iOS & Android'],
        },
        {
            icon: <FaCode />,
            title: 'API Development & Integration',
            desc: 'Robust RESTful APIs built in Node.js and .NET with secure, scalable architectures, OAuth 2.0 security, and comprehensive API documentation.',
            tags: ['RESTful APIs', 'Node.js', '.NET Services', 'OAuth 2.0', 'Swagger Docs'],
        },
        {
            icon: <FaCloud />,
            title: 'Digital Modernisation & Cloud',
            desc: 'Legacy system upgrades, cloud migrations to Azure, microservices architecture design, and modern DevOps practices for continuous delivery.',
            tags: ['Legacy Modernisation', 'Azure Migration', 'Microservices', 'DevOps/CI-CD', 'Architecture Design'],
        },
        {
            icon: <FaCog />,
            title: 'Process Automation & AI',
            desc: 'Intelligent workflow automation, business process optimisation, and AI-powered solutions leveraging Power Platform and Azure AI Services.',
            tags: ['Workflow Automation', 'RPA Solutions', 'AI Integration', 'Business Process', 'Azure AI'],
            techOverlay: '/images/tech_dashboard.png'
        },
        {
            icon: <FaChartBar />,
            title: 'Testing & Application Management',
            desc: 'Comprehensive QA strategies including functional, regression, and UAT testing with ongoing performance monitoring and application lifecycle management.',
            tags: ['Functional Testing', 'Regression Testing', 'UAT', 'Performance Monitoring', 'QA Assurance'],
        },
    ];

    const process = [
        { step: '01', title: 'Discovery', desc: 'We learn your business, goals, constraints, and technical landscape.' },
        { step: '02', title: 'Strategy', desc: 'We design the right architecture and technology approach for your needs.' },
        { step: '03', title: 'Build & Deliver', desc: 'Iterative agile sprints with regular demos, testing, and deployment.' },
        { step: '04', title: 'Support & Grow', desc: 'Continuous monitoring, optimisation, and ongoing partnership.' },
    ];

    return (
        <main className="services-page">
            {/* Banner */}
            <section className="page-banner parallax-wrap">
                <img
                    src="/images/services_hero_bg.png"
                    alt="Services Background"
                    className="parallax-bg"
                    style={{ transform: `translateY(${scrollY * 0.3}px)` }}
                />
                <div className="parallax-overlay" />
                <div className="container">
                    <span className="page-banner-tag">What We Offer</span>
                    <h1 className="page-banner-title fade-in-up">
                        Enterprise IT Services<br /><span className="gradient-text">Built to Scale</span>
                    </h1>
                    <p className="page-banner-subtitle fade-in-up delay-1">
                        From cloud infrastructure to AI automation — comprehensive, end-to-end technology solutions
                        tailored to your unique business requirements.
                    </p>
                </div>
            </section>

            {/* Services Grid */}
            <section className="section">
                <div className="container">
                    <div className="services-full-grid">
                        {services.map((s, i) => (
                            <div key={i} className="glass-card service-full-card">
                                {s.techOverlay && (
                                    <div className="svc-tech-overlay">
                                        <img src={s.techOverlay} alt="Tech Overlay" />
                                    </div>
                                )}
                                <div className="svc-top">
                                    <div className="icon-box">{s.icon}</div>
                                    <h3>{s.title}</h3>
                                </div>
                                <p className="svc-desc">{s.desc}</p>
                                <div className="svc-tags">
                                    {s.tags.map(t => (
                                        <span key={t} className="svc-tag">{t}</span>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Process */}
            <section className="section services-process">
                <div className="container">
                    <div className="process-layout">
                        <div className="process-info">
                            <span className="section-label">How We Work</span>
                            <h2 className="section-title">Our Delivery <span>Process</span></h2>
                            <p className="section-subtitle mb-48">
                                We follow a structured, collaborative approach to ensure your project
                                is delivered on time, within budget, and to the highest quality.
                            </p>

                            <div className="process-steps-list">
                                {process.map((p, i) => (
                                    <div key={i} className="process-item fade-in-up" style={{ animationDelay: `${i * 0.15}s` }}>
                                        <div className="process-num">{p.step}</div>
                                        <div className="process-text">
                                            <h4>{p.title}</h4>
                                            <p>{p.desc}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="process-visual">
                            <div className="process-img-card glass-card hover-zoom">
                                <img src="/images/services_process_whiteboard.png" alt="Team Workshop" />
                                <div className="process-floating-badge animate-float">
                                    <FaRobot />
                                    <span>Agile Methodology</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="section">
                <div className="container">
                    <div className="cta-banner">
                        <h2>Need a Custom <span className="gradient-text">Solution?</span></h2>
                        <p>Every business is unique. Let's discuss your requirements and design the perfect IT solution.</p>
                        <button className="btn-primary" onClick={() => navigate('/contact')}>
                            <FaArrowRight /> Start a Conversation
                        </button>
                    </div>
                </div>
            </section>
        </main>
    );
};

export default ServicesPage;
